import { App, DatePicker, Form, Input, Modal, Select, Space, Tag } from "antd";
import { CloseSquareFilled, SaveFilled } from '@ant-design/icons';
import { useEffect, useMemo, useState } from "react";
import execute from "hooks/execute";
import { protectedResources } from "config/msalConfig";
import dayjs from "dayjs";

export const DataEditor = ({isOpen, dataItem, onDataEditorFinished, onDataEditorCancelled }) => {
    const [form] = Form.useForm();
    const { message } = App.useApp();
    const [aiPlans, setAiPlans] = useState([]);
    const [aiContextLoading, setAiContextLoading] = useState(false);
    const [directPlanAssignment, setDirectPlanAssignment] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const currentPlanStatus = directPlanAssignment?.Status || null;
    const aiPlanStatusOptions = useMemo(() => {
        if (currentPlanStatus === 'Active') {
            return [
                { value: 'Active', label: 'Active' },
                { value: 'Cancelled', label: 'Cancelled' }
            ];
        }

        if (currentPlanStatus === 'Cancelled') {
            return [
                { value: 'Cancelled', label: 'Cancelled' },
                { value: 'Active', label: 'Active' },
                { value: 'Scheduled', label: 'Scheduled' }
            ];
        }

        if (currentPlanStatus === 'Scheduled') {
            return [
                { value: 'Scheduled', label: 'Scheduled' },
                { value: 'Active', label: 'Active' },
                { value: 'Cancelled', label: 'Cancelled' }
            ];
        }

        return [
            { value: 'Active', label: 'Active' },
            { value: 'Scheduled', label: 'Scheduled' }
        ];
    }, [currentPlanStatus]);

    const fetchActiveAiPlans = async () => {
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/plans?status=Published&isActive=true`
            );
            if (response?.success && Array.isArray(response.data)) {
                setAiPlans(response.data);
            } else {
                setAiPlans([]);
            }
        } catch (error) {
            console.error('Error fetching AI plans:', error);
            setAiPlans([]);
        }
    };

    const fetchPlanContext = async (customerId) => {
        if (!customerId) {
            setDirectPlanAssignment(null);
            return null;
        }

        setAiContextLoading(true);
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/assignments?scopeType=CUSTOMER&scopeId=${customerId}&status=Active`
            );

            if (!response?.success || !Array.isArray(response.data) || response.data.length === 0) {
                setDirectPlanAssignment(null);
                form.setFieldsValue({ aiPlanId: undefined, aiPlanStatus: 'Active', aiPlanStartDate: null, aiPlanEndDate: null });
                return null;
            }

            const now = new Date();
            const assignment = response.data.find((a) => {
                const start = a.StartDate ? new Date(a.StartDate) : null;
                const end = a.EndDate ? new Date(a.EndDate) : null;
                return (!start || start <= now) && (!end || end > now);
            }) || response.data[0];

            setDirectPlanAssignment(assignment);
            form.setFieldsValue({
                aiPlanId: assignment.PlanId,
                aiPlanStatus: assignment.Status || 'Active',
                aiPlanStartDate: assignment.StartDate ? dayjs(assignment.StartDate) : null,
                aiPlanEndDate: assignment.EndDate ? dayjs(assignment.EndDate) : null
            });
            return assignment;
        } catch (error) {
            console.error('Error fetching customer plan assignment:', error);
            setDirectPlanAssignment(null);
            return null;
        } finally {
            setAiContextLoading(false);
        }
    };

    const upsertCustomerPlanAssignment = async (customerId, values) => {
        const selectedPlanId = values.aiPlanId ? Number(values.aiPlanId) : null;
        const selectedStatus = values.aiPlanStatus || 'Active';
        const nowIso = new Date().toISOString();
        const scheduledStartIso = values.aiPlanStartDate
            ? (typeof values.aiPlanStartDate.toISOString === 'function'
                ? values.aiPlanStartDate.toISOString()
                : new Date(values.aiPlanStartDate).toISOString())
            : null;
        const scheduledEndIso = values.aiPlanEndDate
            ? (typeof values.aiPlanEndDate.toISOString === 'function'
                ? values.aiPlanEndDate.toISOString()
                : new Date(values.aiPlanEndDate).toISOString())
            : null;
        if (directPlanAssignment?.AssignmentId) {
            if (!selectedPlanId) {
                if (directPlanAssignment.Status !== 'Cancelled') {
                    await execute(
                        'PUT',
                        `${protectedResources.api.baseEndPoint}/ai-subscription/assignments/${directPlanAssignment.AssignmentId}`,
                        {
                            planId: directPlanAssignment.PlanId,
                            status: 'Cancelled',
                            startDate: directPlanAssignment.StartDate || nowIso,
                            endDate: nowIso,
                            reason: 'Customer plan removed from customer editor'
                        }
                    );
                }
                return;
            }

            await execute(
                'PUT',
                `${protectedResources.api.baseEndPoint}/ai-subscription/assignments/${directPlanAssignment.AssignmentId}`,
                {
                    planId: selectedPlanId,
                    status: selectedStatus,
                    startDate: selectedStatus === 'Scheduled' ? scheduledStartIso : nowIso,
                    endDate: selectedStatus === 'Scheduled' ? scheduledEndIso : null,
                    reason: `Updated from customer editor (${selectedStatus})`
                }
            );
            return;
        }

        if (!selectedPlanId) {
            return;
        }

        await execute(
            'POST',
            `${protectedResources.api.baseEndPoint}/ai-subscription/assignments`,
            {
                scopeType: 'CUSTOMER',
                scopeId: Number(customerId),
                planId: selectedPlanId,
                status: selectedStatus,
                startDate: selectedStatus === 'Scheduled' ? scheduledStartIso : nowIso,
                endDate: selectedStatus === 'Scheduled' ? scheduledEndIso : null,
                reason: `Updated from customer editor (${selectedStatus})`
            }
        );
    };

    const handleDataEditFinished = async (values) => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            console.log("handleDataEditFinished");
            var newData = {...dataItem, ...values};
            console.log(newData);
            const isEditMode = Boolean(newData.Customer_ID);
            if(isEditMode){
                // call api to update data
                const response = await execute('PUT',`${protectedResources.api.baseEndPoint}/customers`, newData );
                if(!response?.success){
                    throw new Error(response?.message || 'Failed to update customer');
                }
                newData = {...newData, ...response.data};
                await upsertCustomerPlanAssignment(newData.Customer_ID, values);
            }
            else{
                // call  api to add data
                const response = await execute('POST',`${protectedResources.api.baseEndPoint}/customers`, newData);
                if(!response?.success){
                    throw new Error(response?.message || 'Failed to add customer');
                }
                newData = {...newData, ...response.data};
                const customerId = response?.data?.Customer_ID || newData?.Customer_ID;
                if (customerId) {
                    await upsertCustomerPlanAssignment(customerId, values);
                }
            }
            setIsSaving(false);
            message.success(isEditMode ? "Customer updated successfully" : "Customer added successfully");
            onDataEditorFinished(newData);
        } catch (error) {
            console.error('Failed saving customer + plan assignment:', error);
            setIsSaving(false);
            message.error(error?.message || 'Failed to save customer details');
        }
    }

    const handleDataEditCancelled = () => {
        if (isSaving) return;
        console.log("handleDataEditCancelled");
        onDataEditorCancelled();
    };

    useEffect(() => {
        if (!isOpen) return;

        const hydrate = async () => {
            await fetchActiveAiPlans();
            form.setFieldsValue({
                ...dataItem,
                aiPlanId: undefined,
                aiPlanStatus: 'Active',
                aiPlanStartDate: null,
                aiPlanEndDate: null
            });

            if (dataItem?.Customer_ID) {
                await fetchPlanContext(dataItem.Customer_ID);
            }
        };

        hydrate();
    }, [isOpen, dataItem, form]);

    return (
        <Modal
            open={isOpen}
            title="Customer Editor"
            okText={<Space><SaveFilled />Save</Space> }
            cancelText={<Space><CloseSquareFilled />Cancel</Space> }
            confirmLoading={isSaving}
            okButtonProps={{ autoFocus: true, htmlType: 'submit', loading: isSaving }} //form: 'dataEditorForm', key: 'submit'
            cancelButtonProps={{ disabled: isSaving }}
            maskClosable={!isSaving}
            closable={!isSaving}
            keyboard={!isSaving}
            onCancel={handleDataEditCancelled}
            destroyOnClose
            modalRender={(dom) => (
                <Form
                  layout="horizontal"
                  form={form}
                  name="dataEditorForm"
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  initialValues={{
                    modifier: 'public',
                  }}
                  disabled={isSaving}
                  clearOnDestroy
                  onFinish={handleDataEditFinished}
                >
                  {dom}
                </Form>
            )}>            
            <Form.Item
                name="Customer_ID"
                label="Customer ID"
                hidden>
                <Input />
            </Form.Item>

            <Form.Item
                name="Org_ID"
                label="Organization ID"
                hidden>
                <Input />
            </Form.Item>

            <Form.Item
                name="CustomerName"
                label="Customer Name"
                rules={[{ required: true, message: 'Customer Name is required' }]}>
                <Input />
            </Form.Item>
            <Form.Item
                name="Domain"
                label="Domain"
                rules={[
                    { required: true, message: 'Domain is required' },
                    { pattern: /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: 'Invalid domain name' }]}>
                <Input
                disabled={dataItem.Customer_ID ? true : false}  />
            </Form.Item>
            <Form.Item
                name="Address"
                label="Address"
                rules={[{ required: true, message: 'Address is required' }]}>
                <Input />
            </Form.Item>
            <Form.Item
                name="Workspace_Name"
                label="Workspace Name"
                rules={[{ required: true, message: 'Workspace Name is required' }]}>
                <Input
                disabled={dataItem.Customer_ID ? true : false}  />
            </Form.Item>
            <Form.Item
                name="DatabaseName"
                label="Database"
                rules={[{ required: true, message: 'Database Name is required' }]}>
                <Input
                disabled={dataItem.Customer_ID ? true : false}  />
            </Form.Item>
            <Form.Item
                name="CustEmail"
                label="Customer Email"
                dependencies={['Domain']}
                hasFeedback
                rules={[
                    { required: true, message: 'Customer Email is required' },
                    {type: 'email', message: 'Invalid email address'},
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('Domain') === value.split('@')[1]) {
                                return Promise.resolve();
                            }
                            return Promise.reject('Email domain must match the domain');
                        },
                    }),
                    // validate from server if email is not already taken
                    ({ getFieldValue }) => ({
                        validator: async (_, value) => {
                            if (value) {
                                if(getFieldValue('Customer_ID')){
                                    return Promise.resolve();
                                }
                                try {
                                    const response = await execute('GET', `${protectedResources.api.baseEndPoint}/customerUsers/exists/${value}`);
                                    if (response.success && response.data.exists === false) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(response.message || 'Failed to check if user exists');
                                } catch (error) {
                                    console.error('Error checking if user exists:', error);
                                    return Promise.reject('Error checking if user exists: ' + error.message);
                                }
                            }
                        },
                    }),
                    ]}>
                <Input
                disabled={dataItem.Customer_ID ? true : false} />
            </Form.Item>

            <Form.Item label="AI Plan">
                <Form.Item name="aiPlanId" noStyle>
                    <Select
                        allowClear
                        placeholder="No plan selected"
                        loading={aiContextLoading}
                        options={aiPlans.map((plan) => ({
                            value: plan.PlanId,
                            label: `${plan.PlanName} (${plan.PlanCode})`
                        }))}
                    />
                </Form.Item>
                {directPlanAssignment ? (
                    <div style={{ marginTop: 8 }}>
                        {directPlanAssignment.PlanName ? (
                            <Tag color="gold">{directPlanAssignment.PlanName}</Tag>
                        ) : null}
                        {directPlanAssignment.Status ? (
                            <Tag color={directPlanAssignment.Status === 'Cancelled' ? 'red' : (directPlanAssignment.Status === 'Scheduled' ? 'blue' : 'green')}>
                                {directPlanAssignment.Status}
                            </Tag>
                        ) : null}
                    </div>
                ) : null}
            </Form.Item>

            <Form.Item
                name="aiPlanStatus"
                label="AI Plan Status"
            >
                <Select
                    options={aiPlanStatusOptions}
                />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.aiPlanStatus !== curr.aiPlanStatus}>
                {({ getFieldValue }) => {
                    if (getFieldValue('aiPlanStatus') !== 'Scheduled') {
                        return null;
                    }

                    return (
                        <>
                            <Form.Item
                                name="aiPlanStartDate"
                                label="AI Plan Start Date"
                                rules={[{ required: true, message: 'Start date is required for scheduled status' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                                name="aiPlanEndDate"
                                label="AI Plan End Date"
                                dependencies={['aiPlanStartDate']}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value) {
                                                return Promise.resolve();
                                            }

                                            const startDate = getFieldValue('aiPlanStartDate');
                                            if (!startDate || value.isAfter(startDate)) {
                                                return Promise.resolve();
                                            }

                                            return Promise.reject(new Error('End date must be later than start date'));
                                        }
                                    })
                                ]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </>
                    );
                }}
            </Form.Item>
        </Modal>
    );
};
